import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createProfil, importPdf } from '../api';
import './FirstProfileSetupModal.css';

const LEGACY_SETUP_DONE_KEY = 'cvflow_first_profile_setup_done';
const SEED_KEY = 'cvflow_onboarding_profile_seed';

const LEERE_STAMMDATEN = {
  vorname: '',
  nachname: '',
  email: '',
  telefon: '',
  strasse: '',
  hausnummer: '',
  plz: '',
  ort: '',
};

function leseSeed() {
  try {
    const raw = localStorage.getItem(SEED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setupDoneKey(nutzer) {
  return nutzer?.uid
    ? `cvflow_first_profile_setup_done:${nutzer.uid}`
    : LEGACY_SETUP_DONE_KEY;
}

function Progress({ step }) {
  const labels = ['Stammdaten', 'Lebenslauf'];
  return (
    <div className="fps-progress">
      {labels.map((label, idx) => (
        <div key={label} className={`fps-progress__item${idx <= step ? ' fps-progress__item--active' : ''}`}>
          <span>{idx + 1}</span>
          <strong>{label}</strong>
        </div>
      ))}
    </div>
  );
}

export default function FirstProfileSetupModal({ nutzer, onFertig, initialStep = 0, initialDateiname = null, hasLebenslauf = false }) {
  const seed = useMemo(() => leseSeed(), []);
  const fileRef = useRef(null);
  const abortRef = useRef(null);
  const [step, setStep] = useState(initialStep);
  const [dateiname, setDateiname] = useState(initialDateiname);
  const [stammdaten, setStammdaten] = useState({
    ...LEERE_STAMMDATEN,
    ...(seed?.stammdaten || {}),
    vorname: seed?.stammdaten?.vorname || seed?.name?.vorname || nutzer?.displayName?.split(' ')[0] || '',
    nachname: seed?.stammdaten?.nachname || seed?.name?.nachname || nutzer?.displayName?.split(' ').slice(1).join(' ') || '',
    email: seed?.stammdaten?.email || nutzer?.email || '',
  });
  const [pdfDatei, setPdfDatei] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [justDropped, setJustDropped] = useState(false);
  const [erfolg, setErfolg] = useState(false);

  const stammdatenValid = Object.values(stammdaten).every(v => String(v || '').trim().length > 0);

  function ueberspringen() {
    localStorage.setItem(setupDoneKey(nutzer), '1');
    localStorage.removeItem(SEED_KEY);
    onFertig?.();
  }

  function setFeld(key, value) {
    setStammdaten(prev => ({ ...prev, [key]: value }));
  }

  async function stelleProfilSicher() {
    if (dateiname) return dateiname;
    const payload = Object.fromEntries(
      Object.entries(stammdaten).map(([key, value]) => [key, String(value || '').trim()])
    );
    try {
      const res = await createProfil(payload);
      setDateiname(res.data.dateiname);
      return res.data.dateiname;
    } catch (err) {
      if (err?.response?.status === 409) {
        const name = `${payload.vorname.toLowerCase()}_${payload.nachname.toLowerCase()}`.replace(/ /g, '_');
        setDateiname(name);
        return name;
      }
      throw err;
    }
  }

  async function weiterVonStammdaten() {
    if (!stammdatenValid) return;
    setFehler('');
    setLaedt(true);
    try {
      await stelleProfilSicher();
      setStep(1);
    } catch (err) {
      setFehler(err?.response?.data?.detail || 'Profil konnte nicht erstellt werden.');
    } finally {
      setLaedt(false);
    }
  }

  async function importierePdf() {
    if (!pdfDatei) return;
    setFehler('');
    setLaedt(true);
    abortRef.current = new AbortController();
    try {
      const name = await stelleProfilSicher();
      await importPdf(name, pdfDatei, abortRef.current.signal);
      localStorage.setItem(setupDoneKey(nutzer), '1');
      localStorage.removeItem(SEED_KEY);
      setErfolg(true);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setFehler(err?.response?.data?.detail || 'PDF konnte nicht verarbeitet werden.');
    } finally {
      abortRef.current = null;
      setLaedt(false);
    }
  }

  const abbrechen = useCallback(() => {
    abortRef.current?.abort();
    setLaedt(false);
    setPdfDatei(null);
    setFehler('');
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  if (erfolg) {
    return (
      <div className="fps-overlay" role="dialog" aria-modal="true">
        <div className="fps-modal fps-modal--erfolg">
          <div className="fps-erfolg-icon">✓</div>
          <h2 className="fps-erfolg-title">Lebenslauf übernommen!</h2>
          <p className="fps-erfolg-sub">
            CVFlow hat deinen Lebenslauf eingelesen und dein Profil damit ergänzt.
            Du kannst ihn jederzeit unter <strong>Einstellungen → Profil</strong> einsehen oder ersetzen.
          </p>
          <button className="fps-btn fps-btn--primary fps-erfolg-btn" onClick={() => onFertig?.()}>
            Verstanden →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fps-overlay" role="dialog" aria-modal="true" aria-labelledby="fps-title">
      <div className="fps-modal">

        <div className="fps-skip-header">
          <button type="button" className="fps-skip-btn" onClick={ueberspringen}>
            Überspringen →
          </button>
        </div>

        <div className="fps-body">
          <div className="fps-copy">
            <h2 id="fps-title">
              {step === 0 && <>Ergänze deine Stammdaten.</>}
              {step === 1 && <>Lade deinen Lebenslauf hoch.</>}
            </h2>
            <p>
              {step === 0 && 'Diese Angaben braucht CVFlow für vollständige Bewerbungsunterlagen. Deinen Namen haben wir aus dem Onboarding übernommen.'}
              {step === 1 && 'Lade dein Lebenslauf-PDF hoch – CVFlow liest es automatisch aus.'}
            </p>
          </div>

          {fehler && <div className="fps-error">{fehler}</div>}

          {step === 0 && (
            <div className="fps-grid">
              {[
                ['vorname', 'Vorname', 'given-name'], ['nachname', 'Nachname', 'family-name'], ['email', 'E-Mail', 'email'],
                ['telefon', 'Telefon', 'tel'], ['strasse', 'Straße', 'address-line1'], ['hausnummer', 'Hausnummer', 'address-line2'],
                ['plz', 'PLZ', 'postal-code'], ['ort', 'Wohnort', 'address-level2'],
              ].map(([key, label, autoComplete]) => (
                <label key={key} className="fps-field">
                  <span>{label}</span>
                  <input autoComplete={autoComplete} value={stammdaten[key]} onChange={e => setFeld(key, e.target.value)} />
                </label>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="fps-upload-step">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: 'none' }}
                onChange={e => setPdfDatei(e.target.files?.[0] || null)}
              />
              {laedt ? (
                <div className="fps-upload fps-upload--loading">
                  <span className="fps-upload__spinner" />
                  <strong>Lebenslauf wird analysiert …</strong>
                  <small>Das dauert meist nur wenige Sekunden.</small>
                  <button type="button" className="fps-abort-btn" onClick={abbrechen}>
                    Abbrechen
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={`fps-upload${pdfDatei ? ' fps-upload--ready' : ''}${dragOver ? ' fps-upload--dragover' : ''}${justDropped ? ' fps-upload--accepted' : ''}`}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                  onDrop={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file && (file.type === 'application/pdf' || file.name.endsWith('.pdf'))) {
                      setPdfDatei(file);
                      setJustDropped(true);
                      setTimeout(() => setJustDropped(false), 1200);
                    }
                  }}
                >
                  <span className="fps-upload__icon">{justDropped ? '✓' : 'PDF'}</span>
                  <strong>{dragOver ? 'PDF hier ablegen' : justDropped ? 'Datei angenommen!' : pdfDatei ? pdfDatei.name : 'Lebenslauf-PDF hochladen'}</strong>
                  <small>{pdfDatei && !justDropped ? 'Klicken oder Datei hierher ziehen' : justDropped ? 'Bereit zum Hochladen' : 'PDF auswählen oder hierher ziehen'}</small>
                </button>
              )}
              {!laedt && hasLebenslauf && !pdfDatei && (
                <p className="fps-upload-hint fps-upload-hint--warn">
                  ⚠️ Du hast bereits einen Lebenslauf gespeichert. Eine neue Datei ersetzt ihn vollständig.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="fps-actions">
          {step > initialStep && <button type="button" className="fps-btn fps-btn--ghost" onClick={() => setStep(s => s - 1)} disabled={laedt}>Zurück</button>}
          {step === 0 && <button type="button" className="fps-btn fps-btn--primary" onClick={weiterVonStammdaten} disabled={!stammdatenValid || laedt}>{laedt ? 'Speichert...' : 'Weiter'}</button>}
          {step === 1 && !pdfDatei && <button type="button" className="fps-btn fps-btn--ghost" onClick={ueberspringen} disabled={laedt}>Später einfügen</button>}
          {step === 1 && pdfDatei && <button type="button" className="fps-btn fps-btn--primary" onClick={importierePdf} disabled={laedt}>{laedt ? 'Wird hochgeladen …' : 'Lebenslauf hochladen →'}</button>}
        </div>
      </div>
    </div>
  );
}
